import { Utility } from 'src/app/shared/utility';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PermissionValues } from 'src/app/models/permission.model';
import { User } from 'src/app/models/user';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { EndpointFactory } from './endpoint-factory.service';
import { LocalStoreManager } from './local-store-manager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AppConfigService } from 'src/app/app-config.service';
import { ReadConfigService } from '../common/read-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  darkKeys: any;
  public get loginUrl() { return this.configurations.loginUrl; }
  public get homeUrl() { return this.configurations.homeUrl; }

  public loginRedirectUrl: string;
  public logoutRedirectUrl: string;
  public reLoginDelegate: () => void;
  private orgChangedSubject = new Subject<any>();
  private allowChange = new Subject<boolean>();

  constructor(private router: Router, private configurations: ConfigurationService,
    private endpointFactory: EndpointFactory,
    protected readConfigService: ReadConfigService,
    private localStorage: LocalStoreManager,
    private appConfig: AppConfigService) {
  }

  readonly cryptoKey = this.readConfigService.getcryptoKey();
  // Shared Service used as login verifier
  private LoggedInCheck = new Subject<any>();
  private userCheck = new Subject<any>();
  private orgCount = new Subject<any>();
  checkLoggedIn$ = this.LoggedInCheck.asObservable();
  checkUser$ = this.userCheck.asObservable();
  checkorgCount$ = this.orgCount.asObservable();
  config: any = this.appConfig.getConfig();
  apiUrl = this.config !== undefined ? this.config.API_URL : '';

  loggedInChecker(change: any) {
    this.LoggedInCheck.next(change);
  }

  userChecker(user: any) {
    this.userCheck.next(user);
  }

  userOrgCountCheck(count: any) {
    if (count > 0) {
      this.orgCount.next(count);
    }
  }

  sendMessage(orgId: string) {
    this.orgChangedSubject.next({ text: orgId });
  }

  getMessage$(): Observable<any> {
    return this.orgChangedSubject.asObservable();
  }

  redirectLogoutUser() {
    setTimeout(() => {
      this.router.navigate(['/login']);
      window.location.reload();
    }, 200);
  }

  redirectForLogin() {
    this.loginRedirectUrl = this.router.url;
    this.router.navigate([this.loginUrl]);
  }

  reLogin() {
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    if (this.reLoginDelegate) {
      this.reLoginDelegate();
    } else {
      this.redirectForLogin();
    }
  }

  getLoggedInUserInfo(): Observable<any> {
    return this.endpointFactory.getInitialLoginInfo(this.currentOrg)
      .pipe(map((response: any) => response),
        catchError(this.handleError));
  }

  processInitialResponse(response: any) {
    const permissions: PermissionValues[] = response.claims;
    const decodedIdToken = response.values;
    const user = new User();
    user.Id = decodedIdToken.Id;
    user.IsActive = true;
    user.currentOrg = decodedIdToken.currentOrg;
    this.saveUserDetails(user, permissions, decodedIdToken.encryptOrg, decodedIdToken.encryptUsr);
    this.userOrgCountCheck(parseInt(decodedIdToken.multipleOrg, 0));
    sessionStorage.setItem('InitialOrg', this.encryptText(decodedIdToken.orgName));
    this.setCurrentOrgName(decodedIdToken.orgName);
    this.localStorage.saveSyncedSessionData(parseInt(decodedIdToken.multipleOrg, 0), DBkeys.ORG_CNT);
    return user;
  }

  private saveUserDetails(user: User, permissions: PermissionValues[], encOrg: string, encUser: string) {
    this.localStorage.saveSyncedSessionData(this.encryptText(JSON.stringify(user)), DBkeys.CURRENT_USER);
    this.saveCurrentPermissions(permissions);
    if (encOrg) {
      this.localStorage.saveSyncedSessionData(encOrg.replace(/["']/g, ''), DBkeys.ENC_ORGID);
    }
    if (encUser) {
      this.localStorage.saveSyncedSessionData(encUser.replace(/["']/g, ''), DBkeys.ENC_USER);
    }
    this.localStorage.saveSyncedSessionData(this.encryptText(JSON.stringify(user.currentOrg)), DBkeys.CURRENT_ORG);
  }

  public saveCurrentOrg(orgId: number) {
    this.localStorage.saveSyncedSessionData(this.encryptText(JSON.stringify(orgId)), DBkeys.CURRENT_ORG);
  }

  setEncCurrentOrg(encOrg: string) {
    this.localStorage.saveSyncedSessionData(encOrg.replace(/["']/g, ''), DBkeys.ENC_ORGID);
  }

  public saveCurrentPermissions(accesses: any) {
    const mergedRoleClaims = accesses.reduce((m, o) => {
      const access = m.find(p => p.claim === o.claim);
      if (access) {
        const roles = access.roleName.split(',');
        const newRoles = o.roleName.split(',');
        newRoles.forEach(element => {
          if (!roles.some(x => x === element)) {
            roles.push(element);
          }
        });
        access.roleName = roles.join(',');
      } else {
        m.push(o);
      }
      return m;
    }, []);

    this.localStorage.saveSyncedSessionData(this.encryptText(JSON.stringify(mergedRoleClaims)), DBkeys.CURRENT_PERMISSIONS);
  }

  logout(manual: boolean): void {
    this.localStorage.deleteData(DBkeys.ACCESS_TOKEN);
    this.localStorage.deleteData(DBkeys.REFRESH_TOKEN);
    this.localStorage.deleteData(DBkeys.TOKEN_EXPIRES_IN);
    this.localStorage.deleteData(DBkeys.CURRENT_USER);
    this.localStorage.deleteData(DBkeys.CURRENT_ORG);
    this.localStorage.deleteData(DBkeys.CURRENT_PERMISSIONS);
    this.localStorage.deleteData(DBkeys.ENC_ORGID);
    this.localStorage.deleteData(DBkeys.ENC_USER);
    this.localStorage.deleteData(DBkeys.DARK_FEATURE);
    this.configurations.clearLocalChanges();
    const message = manual ? 'You have been logged out.' : 'Due to inactivity your session was terminated. Please login again.';
    this.localStorage.saveSyncedSessionData(message, DBkeys.LOGOUT_MESSAGE_KEY);
  }

  get currentUser(): User | null {
    if (this.localStorage !== undefined) {
      const user = JSON.parse(this.decryptText(this.localStorage.getDataObject<User>(DBkeys.CURRENT_USER)));
      return user;
    } else {
      return null;
    }
  }

  get currentOrg(): any | null {
    if (this.localStorage !== undefined) {
      return parseInt(JSON.parse(this.decryptText(this.localStorage.getDataObject<any>(DBkeys.CURRENT_ORG))) || 0, 0);
    }
    return null;
  }

  get currentPermissions(): any | null {
    if (this.localStorage !== undefined) {
      const permissions: any = JSON.parse(this.decryptText(this.localStorage.getDataObject<any>(DBkeys.CURRENT_PERMISSIONS))) || [];
      const memberUpload = permissions.find((s: any) => s.claim === 'Member Document Upload');
      const newId = permissions[permissions.length - 1].id + 1;
      const featureName = this.config.SubTasks['Uploads'];
      if (!(permissions.some((s: any) => s.claim === 'Uploads'))) {
        if (memberUpload) {
          permissions.push({
            id: newId, claim: featureName[0], roleName: memberUpload.roleName, role: memberUpload.role, roleIds: memberUpload.roleIds
          });
        }
        const index = permissions.findIndex((element: any) => element.claim === 'View Employer Documents');
        if (index > -1) {
          permissions[index].claim = 'Employer Documents';
        }
      }
      return permissions;
    }
    return null;
  }

  setCurrentOrgName(value: any) {
    sessionStorage.setItem('currentOrgName', this.encryptText(value));
  }

  getCurrentOrgName() {
    return this.decryptText(sessionStorage.getItem('currentOrgName'));
  }

  getAccessId(value: any) {
    const currPermission = this.currentPermissions;
    return currPermission.find((s: any) => value === s.claim);
  }

  getAccessLevel(permission: any) {
    const currPermission = this.currentPermissions;
    const claims = currPermission.filter((x: any) => this.replaceSpace(x.claim) === this.replaceSpace(permission));
    if (claims && claims.length > 0 && claims[0].roleName) {
      return claims[0].roleName.split(',');
    }
    return [];
  }

  replaceSpace(link: any) {
    return Utility.lowerString(link).replace(/ /g, '-');
  }

  splitUrls(url: any) {
    return url.split('/').filter((x: any) => x !== '');
  }

  getAccessOperatiorsForTab(tabName: string) {
    const access = { Add: false, Edit: false, View: false, Delete: false };
    const roles = this.getAccessLevel(tabName);
    access.Add = this.valueExists(roles, '1');
    access.View = this.valueExists(roles, '2');
    access.Edit = this.valueExists(roles, '3');
    access.Delete = this.valueExists(roles, '4');
    return access;
  }

  getAccessOperations(position: any) {
    const access = { Add: false, Edit: false, View: false, Delete: false };
    const spltUrls = this.splitUrls(this.router.url);
    if (spltUrls.length >= position) {
      const roles = this.getAccessLevel(spltUrls[position - 1]);
      access.Add = this.valueExists(roles, '1');
      access.View = this.valueExists(roles, '2');
      access.Edit = this.valueExists(roles, '3');
      access.Delete = this.valueExists(roles, '4');
    }
    return access;
  }

  valueExists(arr: any[], condition: any) {
    return arr.some(x => x === condition);
  }

  getInitialOrg() {
    return this.decryptText(sessionStorage.getItem('InitialOrg'));
  }

  getDarkKeyValue(value: string): any {
    value = this.replaceSpace(value) === 'contact-information' ? 'Account' : value;
    this.darkKeys = this.currentPermissions;
    let canEnable = false;
    if (this.darkKeys === undefined) {
      return canEnable;
    }
    this.darkKeys.some((element: any) => {
      if (this.replaceSpace(element.claim) === this.replaceSpace(value)) {
        canEnable = true;
      }
    });
    return canEnable;
  }

  getDarkFeatureValue() {
    const currPermissions = this.currentPermissions;
    currPermissions.map((x: any) => { x.roleName = x.roleName.split(','); return x; });
    const account = currPermissions.find((x: any) => x.claim === 'Account' && x.roleName.length > 0);
    const home = currPermissions.find((x: any) => x.claim === 'Home' && x.roleName.length > 0);
    sessionStorage.setItem('DFLoaded', 'true');
    sessionStorage.setItem('uploadsFeature', account && !home ? 'true' : 'false');
    this.getUserRedirectPage();
  }

  getUserRedirectPage() {
    const roleNames = this.getAccessLevel('UploadsVersion');
    const isHomeActive = this.getAccessLevel('Home');
    const uploadsFeature = roleNames.length > 0 ? 'true' : 'false';
    if (uploadsFeature && uploadsFeature === 'true' || !(isHomeActive && isHomeActive.length > 0)) {
      this.router.navigate(['/account']);
    } else {
      this.router.navigate(['/home']);
    }
  }

  redirectToUserError() {
    this.router.navigate(['/forbidden'], { queryParams: { usererror: true } });
  }

  get isLoggedIn(): boolean {
    const isLoggedIn = sessionStorage.getItem('access_token') != null;
    return isLoggedIn;
  }

  get userId(): number {
    if (this.currentUser) {
      return this.currentUser.Id;
    }
    return 0;
  }

  sendDropDownMessage(allow: boolean) {
    this.allowChange.next(allow);
  }

  isInEditMode$(): Observable<any> {
    return this.allowChange.asObservable();
  }

  allowChangeDDwn(allow: boolean) {
    this.localStorage.saveSyncedSessionData(allow, DBkeys.ALLOW_CHG);
  }

  formChanged(isChanged: boolean) {
    this.sendDropDownMessage(isChanged);
    this.allowChangeDDwn(isChanged);
  }

  goToLogin() {
    this.router.navigate(['/login']);
  }

  encryptText(text: any): any {
    return CryptoJS.AES.encrypt(text, this.cryptoKey).toString();
  }

  decryptText(text: any): any {
    return text != null ? CryptoJS.AES.decrypt(text, this.cryptoKey).toString(CryptoJS.enc.Utf8) : text;
  }

  handleError(error: HttpErrorResponse) {
    return throwError(error || 'Server error');
  }
}
